// animalQuestions — WHAT WE ASK ABOUT AN ANIMAL, BY WHAT KIND OF ANIMAL IT IS
// (WEL-173).
//
// ⚠️ THIS FILE EXISTS TWICE, BYTE FOR BYTE:
//      apps/api/src/lib/animalQuestions.ts
//      apps/web/src/lib/animalQuestions.ts
//    The web cannot import across the workspace boundary, so the matrix is
//    restated — and `animalQuestions.lockstep.test.ts` (API suite) fails the
//    build the moment the two copies differ by a single byte. Edit one, copy it
//    over the other. It therefore imports NOTHING and uses no path alias.
//
// Why a matrix. Andrea (WEL-173) found the addendum's animal table for a cat
// showing only Type / Sex / Spayed-neutered. Miles (2026-09-29): "this should be
// contextual. Hamster we don't care the age, breed etc. Cat we don't care about
// the breed, we care about age and color. Dogs we want age, breed and weight."
// So the questions depend on the animal, and BOTH household forms — the Animal
// Addendum form and the pre-lease intake's animals section — ask exactly what
// this table says, the API normalizer keeps exactly what it says (a cat never
// stores a breed, a hamster never stores an age), and the documents print what
// was kept.
//
//   kind            asked (* = required to submit)
//   ─────────────── ───────────────────────────────────────────────────────────
//   dog             name*, breed*, weight*, age*, color, sex, spayed/neutered,
//                   license #, photo
//   cat             name*, age*, color*, sex, spayed/neutered, license #, photo
//   bird            species* (what kind of bird), name, photo
//   small animal    what kind* (hamster, guinea pig, rabbit…), name
//   reptile         species*, name
//   fish/aquarium   tank size in gallons*, name ("Aquarium" when unnamed)
//   something else  what kind*, name, weight
//
// The STORED type stays 'dog' | 'cat' | 'other' — fees, the AppFolio sync,
// DocuSeal and the lease all key on it and are untouched. The finer kind rides
// alongside as `otherKind` (only for 'other').
//
// Backward compatibility. An 'other' animal with NO `otherKind` — stored before
// this change, sent by a form bundle opened before the deploy, recorded by the
// Accommodations (RRA) forms, or seeded from AppFolio — is `other_legacy` and
// keeps EXACTLY the field set it always had, so nothing already stored changes
// shape. Likewise an animal with no type at all keeps the old untyped rule.
//
// The photo row is a flag on purpose (lead, 2026-09-29): the addendum's "email
// Lessor a current photograph" sentence names only animals whose kind is asked
// for a photo, so widening the photo to more kinds is a one-line change here.

/**
 * Sent by both forms as `questionsVersion` on every submission built from this
 * matrix. The API enforces the matrix's REQUIRED answers only on a submission
 * that carries it: a form bundle opened before the deploy (no version) is still
 * accepted by the rules it was built with, so a household mid-form is never
 * stranded by a deploy. Whatever the version, fields that do not apply to the
 * animal are dropped.
 */
export const ANIMAL_QUESTIONS_VERSION = 2;

/** The stored animal type — unchanged by WEL-173. */
export type AnimalBaseType = 'dog' | 'cat' | 'other';

/** What an "other" animal actually is. */
export type OtherAnimalKind = 'bird' | 'small_animal' | 'reptile' | 'fish' | 'something_else';

export const OTHER_ANIMAL_KINDS: readonly OtherAnimalKind[] = [
  'bird',
  'small_animal',
  'reptile',
  'fish',
  'something_else',
];

/** The choices under "Other", in the order the forms list them. */
export const OTHER_ANIMAL_KIND_CHOICES: ReadonlyArray<{ value: OtherAnimalKind; label: string }> = [
  { value: 'bird', label: 'Bird' },
  { value: 'small_animal', label: 'Small caged animal (hamster, guinea pig, rabbit, gerbil…)' },
  { value: 'reptile', label: 'Reptile (snake, lizard, turtle…)' },
  { value: 'fish', label: 'Fish / aquarium' },
  { value: 'something_else', label: 'Something else' },
];

/**
 * The ONE "What kind of animal?" choice both forms show. It maps onto the stored
 * pair: 'dog' / 'cat' → that type; every other value → type 'other' with that
 * `otherKind`. There is deliberately no bare "Other": an 'other' animal with no
 * kind (a pre-WEL-173 record, an AppFolio seed) shows this choice unanswered,
 * and the form asks for it.
 */
export type AnimalKindChoice = 'dog' | 'cat' | OtherAnimalKind;

export const ANIMAL_KIND_CHOICES: ReadonlyArray<{ value: AnimalKindChoice; label: string }> = [
  { value: 'dog', label: 'Dog' },
  { value: 'cat', label: 'Cat' },
  ...OTHER_ANIMAL_KIND_CHOICES,
];

/** The choice an animal's stored pair selects ('' = not chosen yet). */
export function animalKindChoiceOf(a: {
  type?: string | null | undefined;
  otherKind?: string | null | undefined;
}): AnimalKindChoice | '' {
  if (a.type === 'dog' || a.type === 'cat') return a.type;
  if (a.type === 'other' && isOtherAnimalKind(a.otherKind)) return a.otherKind;
  return '';
}

/** The stored pair a choice means. */
export function animalTypeForChoice(choice: AnimalKindChoice): {
  type: AnimalBaseType;
  otherKind?: OtherAnimalKind;
} {
  if (choice === 'dog' || choice === 'cat') return { type: choice };
  return { type: 'other', otherKind: choice };
}

/** How an age is counted. Absent on everything stored before WEL-173. */
export type AnimalAgeUnit = 'years' | 'months';

export const ANIMAL_AGE_UNITS: readonly AnimalAgeUnit[] = ['years', 'months'];

/** The question set an animal is asked. */
export type AnimalQuestionKind = 'dog' | 'cat' | OtherAnimalKind | 'other_legacy';

/** Every answer a form can collect about one animal. */
export type AnimalField =
  | 'name'
  | 'otherDescription'
  | 'breed'
  | 'color'
  | 'weightLbs'
  | 'age'
  | 'gender'
  | 'spayedNeutered'
  | 'tankGallons'
  | 'licenseNumber'
  | 'photo';

/** The order the forms show the fields in. */
export const ANIMAL_FIELD_ORDER: readonly AnimalField[] = [
  'name',
  'otherDescription',
  'breed',
  'color',
  'weightLbs',
  'age',
  'gender',
  'spayedNeutered',
  'tankGallons',
  'licenseNumber',
  'photo',
];

export type AnimalAsk = 'required' | 'optional';

/** Field → whether it is asked, and if so whether it is required. Absent = not asked. */
export type AnimalQuestionSet = Readonly<Partial<Record<AnimalField, AnimalAsk>>>;

/** THE MATRIX. See the table in the header. */
export const ANIMAL_QUESTIONS: Readonly<Record<AnimalQuestionKind, AnimalQuestionSet>> = {
  dog: {
    name: 'required',
    breed: 'required',
    color: 'optional',
    weightLbs: 'required',
    age: 'required',
    gender: 'optional',
    spayedNeutered: 'optional',
    licenseNumber: 'optional',
    photo: 'optional',
  },
  cat: {
    name: 'required',
    color: 'required',
    age: 'required',
    gender: 'optional',
    spayedNeutered: 'optional',
    licenseNumber: 'optional',
    photo: 'optional',
  },
  bird: {
    name: 'optional',
    otherDescription: 'required',
    photo: 'optional',
  },
  small_animal: {
    name: 'optional',
    otherDescription: 'required',
  },
  reptile: {
    name: 'optional',
    otherDescription: 'required',
  },
  fish: {
    name: 'optional',
    tankGallons: 'required',
  },
  something_else: {
    name: 'optional',
    otherDescription: 'required',
    weightLbs: 'optional',
  },
  // The pre-WEL-173 "other" field set, kept verbatim for animals with no kind.
  other_legacy: {
    name: 'optional',
    otherDescription: 'optional',
    weightLbs: 'optional',
    age: 'optional',
    gender: 'optional',
    spayedNeutered: 'optional',
    licenseNumber: 'optional',
    photo: 'optional',
  },
};

/** The pre-WEL-173 rule for an animal with no type at all (no form produces one;
 *  kept so a stored or staff-recorded untyped animal normalizes as it always did). */
const UNTYPED_QUESTIONS: AnimalQuestionSet = {
  name: 'optional',
  weightLbs: 'optional',
  age: 'optional',
  gender: 'optional',
  spayedNeutered: 'optional',
  licenseNumber: 'optional',
  photo: 'optional',
};

export function isAnimalBaseType(v: unknown): v is AnimalBaseType {
  return v === 'dog' || v === 'cat' || v === 'other';
}

export function isOtherAnimalKind(v: unknown): v is OtherAnimalKind {
  return typeof v === 'string' && (OTHER_ANIMAL_KINDS as readonly string[]).includes(v);
}

export function isAnimalAgeUnit(v: unknown): v is AnimalAgeUnit {
  return v === 'years' || v === 'months';
}

/** Which question set an animal gets. null = no type chosen yet. */
export function animalQuestionKind(a: {
  type?: string | null | undefined;
  otherKind?: string | null | undefined;
}): AnimalQuestionKind | null {
  if (a.type === 'dog') return 'dog';
  if (a.type === 'cat') return 'cat';
  if (a.type === 'other') return isOtherAnimalKind(a.otherKind) ? a.otherKind : 'other_legacy';
  return null;
}

/** The question set for a kind (the untyped rule for null). */
export function animalQuestionSet(kind: AnimalQuestionKind | null): AnimalQuestionSet {
  return kind === null ? UNTYPED_QUESTIONS : ANIMAL_QUESTIONS[kind];
}

/** Whether `field` is asked at all for this kind. */
export function asksAnimalField(kind: AnimalQuestionKind | null, field: AnimalField): boolean {
  return animalQuestionSet(kind)[field] !== undefined;
}

/** Whether `field` must be answered before this kind can be submitted. */
export function requiresAnimalField(kind: AnimalQuestionKind | null, field: AnimalField): boolean {
  return animalQuestionSet(kind)[field] === 'required';
}

// ---------------------------------------------------------------------------
// What is still missing
// ---------------------------------------------------------------------------

/** The answers, as both forms and the API hold them once parsed. */
export interface AnimalAnswers {
  type?: string | null | undefined;
  otherKind?: string | null | undefined;
  name?: string | null | undefined;
  otherDescription?: string | null | undefined;
  breed?: string | null | undefined;
  color?: string | null | undefined;
  weightLbs?: number | null | undefined;
  age?: number | null | undefined;
  gender?: string | null | undefined;
  spayedNeutered?: boolean | null | undefined;
  tankGallons?: number | null | undefined;
  licenseNumber?: string | null | undefined;
}

/** A required answer that is missing. 'type' / 'otherKind' come first. */
export type AnimalGap = AnimalField | 'type' | 'otherKind';

function answered(a: AnimalAnswers, field: AnimalField): boolean {
  switch (field) {
    case 'weightLbs':
    case 'age':
    case 'tankGallons': {
      const n = a[field];
      return typeof n === 'number' && Number.isFinite(n) && n > 0;
    }
    case 'spayedNeutered':
      return typeof a.spayedNeutered === 'boolean';
    case 'photo':
      // A photo is never required, and it is not part of the answers.
      return true;
    default: {
      const s = a[field];
      return typeof s === 'string' && s.trim().length > 0;
    }
  }
}

/**
 * The required answers this animal is still missing, in form order. Empty ⇒
 * complete. Choosing the type (and, for "other", the kind) comes first: until
 * then nothing else can be judged.
 */
export function missingAnimalAnswers(a: AnimalAnswers): AnimalGap[] {
  if (!isAnimalBaseType(a.type)) return ['type'];
  if (a.type === 'other' && !isOtherAnimalKind(a.otherKind)) return ['otherKind'];
  const kind = animalQuestionKind(a);
  const set = animalQuestionSet(kind);
  return ANIMAL_FIELD_ORDER.filter((f) => set[f] === 'required' && !answered(a, f));
}

// ---------------------------------------------------------------------------
// Words — labels, prompts, and how a kept answer reads on a document
// ---------------------------------------------------------------------------

/** The word for an unnamed animal of this kind in "your ___". */
function kindWord(kind: AnimalQuestionKind | null): string {
  switch (kind) {
    case 'dog':
      return 'dog';
    case 'cat':
      return 'cat';
    case 'bird':
      return 'bird';
    case 'reptile':
      return 'reptile';
    case 'fish':
      return 'aquarium';
    default:
      return 'animal';
  }
}

/** Which animal a prompt is about, when there may be several on the form. */
export interface AnimalRef {
  /** 0-based position on the form. */
  index: number;
  /** How many animals the form holds. */
  total: number;
}

function possessive(noun: string): string {
  return `${noun}’s`;
}

/**
 * The plain, specific sentence telling the household what to fill in — the
 * form shows it beside the disabled Submit button, and the API returns the same
 * words in a 400. Never an id: an animal is its name, "your dog", or "animal 2".
 */
export function animalGapPrompt(gap: AnimalGap, a: AnimalAnswers, ref: AnimalRef): string {
  const kind = animalQuestionKind(a);
  const name = (a.name ?? '').trim();
  const single = ref.total <= 1;
  const n = ref.index + 1;
  // Subject form: "Biscuit" / "your dog" / "animal 2".
  const subject = name || (single ? `your ${kindWord(kind)}` : `animal ${n}`);
  // "it" form with a lead-in when several unnamed animals need telling apart.
  const lead = !name && !single ? `For animal ${n}, ` : '';
  const it = name || 'it';
  const cap = (s: string): string => (lead ? s : s.charAt(0).toUpperCase() + s.slice(1));

  switch (gap) {
    case 'type':
    case 'otherKind':
      return `${lead}${cap(`choose what kind of animal ${it} is.`)}`;
    case 'name':
      return `What is ${single ? `your ${kindWord(kind)}` : `animal ${n}`} called?`;
    case 'otherDescription':
      if (kind === 'bird') return `${lead}${cap(`what kind of bird is ${it}?`)}`;
      if (kind === 'small_animal') return `${lead}${cap(`what kind of small animal is ${it}?`)}`;
      if (kind === 'reptile') return `${lead}${cap(`what kind of reptile is ${it}?`)}`;
      return `${lead}${cap(`tell us what kind of animal ${it} is.`)}`;
    case 'breed':
      return `Tell us ${possessive(subject)} breed.`;
    case 'color':
      return `What color is ${subject}?`;
    case 'weightLbs':
      return `How much does ${subject} weigh, in pounds?`;
    case 'age':
      return `How old is ${subject}?`;
    case 'tankGallons':
      return name
        ? `How big is ${possessive(name)} tank, in gallons?`
        : `${lead}${cap('how big is the tank, in gallons?')}`;
    case 'gender':
      return `Is ${subject} male or female?`;
    case 'spayedNeutered':
      return `Is ${subject} spayed or neutered?`;
    case 'licenseNumber':
      return `What is ${possessive(subject)} license number?`;
    case 'photo':
      return `Add a photo of ${subject}.`;
  }
}

/** The form label for a field, for this kind (optional fields say so, once a
 *  kind is chosen — before that nothing is known to be optional). */
export function animalFieldLabel(kind: AnimalQuestionKind | null, field: AnimalField): string {
  const optional =
    kind !== null && animalQuestionSet(kind)[field] === 'optional' ? ' (optional)' : '';
  switch (field) {
    case 'name':
      return `Name${optional}`;
    case 'otherDescription':
      if (kind === 'bird') return `What kind of bird?${optional}`;
      if (kind === 'small_animal') return `What kind of small animal?${optional}`;
      if (kind === 'reptile') return `What kind of reptile?${optional}`;
      if (kind === 'something_else') return `What is it?${optional}`;
      return `What kind of animal?${optional}`;
    case 'breed':
      return `Breed${optional}`;
    case 'color':
      return `Color / markings${optional}`;
    case 'weightLbs':
      return `Weight in pounds${optional}`;
    case 'age':
      return `Age${optional}`;
    case 'gender':
      return `Sex${optional}`;
    case 'spayedNeutered':
      return `Spayed / neutered?${optional}`;
    case 'tankGallons':
      return `Tank size, in gallons${optional}`;
    case 'licenseNumber':
      return `License number${optional}`;
    case 'photo':
      return `Photo${optional}`;
  }
}

/** A placeholder that shows the household what kind of answer fits. */
export function animalFieldPlaceholder(kind: AnimalQuestionKind | null, field: AnimalField): string {
  if (field === 'otherDescription') {
    if (kind === 'bird') return 'e.g. cockatiel, parakeet';
    if (kind === 'small_animal') return 'e.g. hamster, guinea pig, rabbit';
    if (kind === 'reptile') return 'e.g. bearded dragon, ball python';
    if (kind === 'something_else') return 'e.g. pot-bellied pig';
    return 'e.g. rabbit';
  }
  if (field === 'color') return kind === 'cat' ? 'e.g. gray tabby' : 'e.g. black with a white chest';
  if (field === 'breed') return 'e.g. Labrador mix';
  if (field === 'tankGallons') return 'e.g. 20';
  return '';
}

/** "1 year" / "9 years" / "1 month" / "4 months". '' when there is no age. */
export function formatAnimalAge(age: number | null | undefined, unit: AnimalAgeUnit): string {
  if (typeof age !== 'number' || !Number.isFinite(age) || age <= 0) return '';
  const word = unit === 'months' ? 'month' : 'year';
  return `${age} ${age === 1 ? word : `${word}s`}`;
}

/** "1 gallon" / "20 gallons". '' when there is no tank size. */
export function formatTankGallons(gallons: number | null | undefined): string {
  if (typeof gallons !== 'number' || !Number.isFinite(gallons) || gallons <= 0) return '';
  return `${gallons} ${gallons === 1 ? 'gallon' : 'gallons'}`;
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/** "Cockatiel" → "cockatiel" (a phone capitalizes the first letter of whatever
 *  is typed; the rest is left as the household wrote it). */
function uncapitalize(s: string): string {
  return s ? s.charAt(0).toLowerCase() + s.slice(1) : s;
}

/** What the household typed as the species/kind, trimmed ('' when none). */
function described(a: { otherDescription?: string | null | undefined }): string {
  return (a.otherDescription ?? '').trim();
}

/**
 * The Type a reader sees on a document or a staff surface:
 *   Dog · Cat · "Bird — cockatiel" · "Small animal — hamster" ·
 *   "Reptile — ball python" · Fish · "Pot-bellied pig" (something else, and
 *   every pre-WEL-173 "other": the kind in the household's words, never the
 *   word "other"). '' for an untyped animal.
 */
export function animalKindLabel(a: {
  type?: string | null | undefined;
  otherKind?: string | null | undefined;
  otherDescription?: string | null | undefined;
}): string {
  const kind = animalQuestionKind(a);
  const desc = described(a);
  const withDesc = (word: string): string =>
    desc && desc.toLowerCase() !== word.toLowerCase() ? `${word} — ${uncapitalize(desc)}` : word;
  switch (kind) {
    case 'dog':
      return 'Dog';
    case 'cat':
      return 'Cat';
    case 'bird':
      return withDesc('Bird');
    case 'small_animal':
      return withDesc('Small animal');
    case 'reptile':
      return withDesc('Reptile');
    case 'fish':
      return 'Fish';
    case 'something_else':
    case 'other_legacy':
      return desc ? capitalize(desc) : 'Animal';
    default:
      return '';
  }
}

/**
 * The noun a sentence uses for the animal ("adds a cockatiel", "a photograph of
 * a hamster"): the household's own word for what it is. A fish
 * is "fish" when it has a name and "aquarium" when it does not — an unnamed
 * tank is the thing being approved.
 */
export function animalKindNoun(a: {
  type?: string | null | undefined;
  otherKind?: string | null | undefined;
  otherDescription?: string | null | undefined;
  name?: string | null | undefined;
}): string {
  const kind = animalQuestionKind(a);
  const desc = uncapitalize(described(a));
  switch (kind) {
    case 'dog':
      return 'dog';
    case 'cat':
      return 'cat';
    case 'fish':
      return (a.name ?? '').trim() ? 'fish' : 'aquarium';
    case 'bird':
      return desc || 'bird';
    case 'reptile':
      return desc || 'reptile';
    case 'small_animal':
    case 'something_else':
    case 'other_legacy':
      return desc || 'animal';
    default:
      return 'animal';
  }
}

/** The name a reader sees for an animal the household did not name: "Aquarium",
 *  "Cockatiel", "Hamster". Named animals are their name. */
export function animalNameOrKind(a: {
  type?: string | null | undefined;
  otherKind?: string | null | undefined;
  otherDescription?: string | null | undefined;
  name?: string | null | undefined;
}): string {
  const name = (a.name ?? '').trim();
  if (name) return name;
  return capitalize(animalKindNoun(a));
}
